/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RoleChoiceDialog
 * ----------------
 * Shown immediately after a successful login when the login response carries
 * the organizer_admin role. It asks where the user wants to land: the organizer
 * dashboard, or their own athlete view.
 *
 * Only rendered for an organizer role — every other user is navigated straight
 * through, so this never adds a step for a normal athlete.
 *
 * The dialog has no dismiss path: both outcomes are one click away, and a
 * "skip" would have gone to /dashboard, which is exactly what "Continue as
 * athlete" already does. Escape and backdrop clicks are inert for the same
 * reason — a silent third route to a duplicate destination.
 *
 * Dawn tokens are applied on the overlay itself rather than inherited, because
 * the dialog mounts over SignInPage, which runs on the app-wide theme.
 */

import React, { useEffect, useRef } from 'react';
import ORGANIZER_TOKENS from '../../theme/organizerTokens';
import {
  ROLE_CHOICE_ORGANIZER,
  ROLE_CHOICE_ATHLETE,
} from '../../utils/roles';
import '../../styles/organizer.css';

function RoleChoiceDialog({ open, firstName, onChoose }) {
  const primaryRef = useRef(null);

  // Move focus into the dialog so the keyboard path works.
  useEffect(() => {
    if (open && primaryRef.current) primaryRef.current.focus();
  }, [open]);

  if (!open) return null;

  const choose = (choice) => onChoose(choice);

  return (
    <div className="org-gate" style={ORGANIZER_TOKENS}>
      <div className="org-dialog" role="dialog" aria-modal="true" aria-labelledby="org-gate-title">
        <span className="org-badge">Role · organizer_admin</span>
        <h2 id="org-gate-title">
          Two ways in{firstName ? `, ${firstName}` : ''}.
        </h2>
        <p className="org-gsub">
          This account holds organizer rights. Pick where to land.
        </p>

        <button
          type="button"
          className="org-opt primary"
          ref={primaryRef}
          onClick={() => choose(ROLE_CHOICE_ORGANIZER)}
        >
          <span className="org-ico"><i className="bi bi-speedometer2" /></span>
          <span>
            <span className="org-k">Continue as organizer</span>
            <span className="org-v">Events, registrations, check-ins, payouts</span>
          </span>
          <span className="org-arw" aria-hidden="true">&rarr;</span>
        </button>

        <button
          type="button"
          className="org-opt"
          onClick={() => choose(ROLE_CHOICE_ATHLETE)}
        >
          <span className="org-ico"><i className="bi bi-person" /></span>
          <span>
            <span className="org-k">Continue as athlete</span>
            <span className="org-v">Your activity, records and streaks</span>
          </span>
          <span className="org-arw" aria-hidden="true">&rarr;</span>
        </button>
      </div>
    </div>
  );
}

export default RoleChoiceDialog;
